import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Globe,
  ArrowRight,
  Shield,
  FileText,
} from 'lucide-react';
import { SpamFileAnalysis } from '../../services/api';

function AuthBadge({ label, result }: { label: string; result?: { result: string; domain?: string } }) {
  if (!result) return <span className="text-xs text-muted-foreground">{label}: n/a</span>;
  const pass = result.result === 'pass';
  const fail = result.result === 'fail' || result.result === 'softfail';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
      pass ? 'bg-emerald-500/10 text-emerald-600' : fail ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'
    }`}>
      {pass ? <CheckCircle className="h-3 w-3" /> : fail ? <XCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
      {label}: {result.result}
      {result.domain && <span className="text-[10px] opacity-70">({result.domain})</span>}
    </span>
  );
}

export default function SpamAnalysisDetail({ files }: { files: SpamFileAnalysis[] }) {
  if (!files || files.length === 0) return <p className="text-sm text-muted-foreground">No analysis data available.</p>;

  return (
    <div className="space-y-4">
      {files.map((file, i) => (
        <div key={i} className="border border-border rounded-xl bg-card overflow-hidden">
          {/* File header */}
          <div className="bg-muted/50 px-4 py-3 flex items-center gap-2 border-b border-border">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground truncate">{file.fileName}</span>
            <span className="text-xs text-muted-foreground ml-auto">{(file.fileSize / 1024).toFixed(1)} KB</span>
          </div>

          <div className="p-4 space-y-4">
            {/* From / To / Subject */}
            <div className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
              <span className="text-muted-foreground font-medium">From:</span>
              <span className="text-foreground truncate">{file.from}</span>
              <span className="text-muted-foreground font-medium">To:</span>
              <span className="text-foreground truncate">{file.to}</span>
              <span className="text-muted-foreground font-medium">Subject:</span>
              <span className="text-foreground font-medium">{file.subject}</span>
              {file.date && <>
                <span className="text-muted-foreground font-medium">Date:</span>
                <span className="text-foreground">{file.date}</span>
              </>}
            </div>

            {/* Auth results */}
            <div className="flex flex-wrap gap-2">
              <AuthBadge label="SPF" result={file.spf} />
              <AuthBadge label="DKIM" result={file.dkim} />
              <AuthBadge label="DMARC" result={file.dmarc} />
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                file.passedThroughSpamHero ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'
              }`}>
                <Shield className="h-3 w-3" />
                SpamHero: {file.passedThroughSpamHero ? 'Yes' : 'No'}
              </span>
            </div>

            {/* Originating IP */}
            {file.originatingIP && (
              <div className="flex items-center gap-2 text-sm">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Origin IP:</span>
                <code className="bg-muted px-2 py-0.5 rounded text-foreground font-mono text-xs">{file.originatingIP}</code>
              </div>
            )}

            {/* Received hops (collapsed by default for brevity) */}
            {file.receivedHops.length > 0 && (
              <details className="group">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  Routing: {file.receivedHops.length} hop{file.receivedHops.length > 1 ? 's' : ''} (click to expand)
                </summary>
                <div className="mt-2 space-y-1 pl-2 border-l-2 border-border">
                  {[...file.receivedHops].reverse().map((hop, j) => (
                    <div key={j} className="flex items-start gap-2 text-xs">
                      <ArrowRight className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        {hop.from && <span className="text-foreground">{hop.from}</span>}
                        {hop.from && hop.by && <span className="text-muted-foreground"> → </span>}
                        {hop.by && <span className="text-foreground">{hop.by}</span>}
                        {hop.timestamp && <span className="text-muted-foreground ml-2">({hop.timestamp})</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Parse error */}
            {file.parseError && (
              <div className="bg-amber-500/10 rounded-lg px-3 py-2 text-xs text-amber-600 flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5" />
                {file.parseError}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
